import React, { useMemo, useState } from "react";
import { toast } from "sonner";

/**
 * Sprint 60.76 — Group Rules composer. Extracted from RawOptimizer.jsx
 * in Sprint 60.132 as part of the sub-component split.
 *
 * Builds structured If/Then + cardinality rules against the current pool.
 * Rule shape sent to backend:
 *   { type, if:[dk_id], then:[dk_id], players:[dk_id], n:int, team, teams, positions }
 */
export default function GroupRulesPanel({ pool, rules, onChange }) {
  const [ruleType, setRuleType] = useState("if_then_exclude");
  const [ifPicks, setIfPicks] = useState([]);
  const [thenPicks, setThenPicks] = useState([]);
  const [countPicks, setCountPicks] = useState([]);
  const [n, setN] = useState(1);
  const [q, setQ] = useState("");
  // Sprint 60.129 — team-scoped rule composer state.
  const [teamPick, setTeamPick] = useState("");
  const [posPicks, setPosPicks] = useState([]);

  const RULE_TYPES = [
    { key: "if_then_exclude", label: "If A · Exclude B", tag: "conditional" },
    { key: "if_then_include", label: "If A · Include B", tag: "conditional" },
    { key: "if_then_at_least_n", label: "If A · ≥N of B", tag: "correlation" },
    { key: "exactly_n",       label: "Exactly N of",     tag: "cardinality" },
    { key: "at_most_n",       label: "At Most N of",     tag: "cardinality" },
    { key: "at_least_n",      label: "At Least N of",    tag: "cardinality" },
    { key: "team_at_least_n", label: "Team · ≥N of Pos", tag: "team" },
    { key: "team_at_most_n",  label: "Team · ≤N of Pos", tag: "team" },
  ];
  const isConditional = ruleType === "if_then_exclude" || ruleType === "if_then_include";
  const isCorrelation = ruleType === "if_then_at_least_n";
  const isTeamRule = ruleType === "team_at_least_n" || ruleType === "team_at_most_n";
  const activeType = RULE_TYPES.find((r) => r.key === ruleType);

  // Sprint 60.80 — Rule Presets. Each preset is a compute function that
  // takes the current pool and returns a rule (or null if the pool
  // doesn't contain the players it needs). Users click and the rule is
  // added instantly with populated dk_ids — no manual composer step.
  const PRESETS = React.useMemo(() => ([
    {
      key: "cap_chalk_stack",
      label: "Cap Chalk Stack ≤ 2",
      hint: "At most 2 of the 5 highest-owned skill players in any lineup",
      build: () => {
        const top = (pool || [])
          .filter((p) => ["RB", "WR", "TE"].includes((p.position || "").toUpperCase()))
          .sort((a, b) => Number(b.our_own || 0) - Number(a.our_own || 0))
          .slice(0, 5)
          .map((p) => String(p.dk_id));
        if (top.length < 3) return null;
        return { type: "at_most_n", players: top, n: 2 };
      },
    },
    {
      key: "min_low_owned",
      label: "≥1 Sub-5% Pivot",
      hint: "Force at least one player under 5% ownership in every lineup",
      build: () => {
        const low = (pool || [])
          .filter((p) => Number(p.our_own || 0) > 0 && Number(p.our_own || 0) < 5)
          .filter((p) => ["RB", "WR", "TE"].includes((p.position || "").toUpperCase()))
          .sort((a, b) => Number(a.our_own || 0) - Number(b.our_own || 0))
          .slice(0, 25)
          .map((p) => String(p.dk_id));
        if (low.length < 3) return null;
        return { type: "at_least_n", players: low, n: 1 };
      },
    },
    {
      key: "no_two_qbs_stack",
      label: "No Duplicate QB Stacks",
      hint: "At most 1 QB from the top-2 highest-owned QBs (spreads QB exposure)",
      build: () => {
        const qbs = (pool || [])
          .filter((p) => (p.position || "").toUpperCase() === "QB")
          .sort((a, b) => Number(b.our_own || 0) - Number(a.our_own || 0))
          .slice(0, 2)
          .map((p) => String(p.dk_id));
        if (qbs.length < 2) return null;
        return { type: "at_most_n", players: qbs, n: 1 };
      },
    },
    {
      key: "premium_rb_cap",
      label: "Cap Premium RBs ≤ 2",
      hint: "At most 2 of the top-6 highest-salary RBs (avoids running back overspend)",
      build: () => {
        const rbs = (pool || [])
          .filter((p) => (p.position || "").toUpperCase() === "RB")
          .sort((a, b) => Number(b.salary || 0) - Number(a.salary || 0))
          .slice(0, 6)
          .map((p) => String(p.dk_id));
        if (rbs.length < 3) return null;
        return { type: "at_most_n", players: rbs, n: 2 };
      },
    },
    {
      key: "dst_diversify",
      label: "Diversify DST",
      hint: "At most 1 of the top-2 highest-owned defenses (locks in DST variance)",
      build: () => {
        const dst = (pool || [])
          .filter((p) => (p.position || "").toUpperCase() === "DST")
          .sort((a, b) => Number(b.our_own || 0) - Number(a.our_own || 0))
          .slice(0, 2)
          .map((p) => String(p.dk_id));
        if (dst.length < 2) return null;
        return { type: "at_most_n", players: dst, n: 1 };
      },
    },
    {
      // Sprint 60.128 — Correlation preset. Pairs the highest-projection
      // QB with ≥2 of their same-team WR/TE. If that QB rosters, force a
      // double-stack around them. Skips silently if pool is too thin.
      key: "qb_double_stack_corr",
      label: "Top QB · ≥2 Same-Team WR/TE",
      hint: "If highest-projection QB rosters, force ≥2 of their same-team WR/TE targets",
      build: () => {
        const qbs = (pool || [])
          .filter((p) => (p.position || "").toUpperCase() === "QB")
          .sort((a, b) => Number(b.our_proj || b.projection || 0) - Number(a.our_proj || a.projection || 0));
        if (qbs.length === 0) return null;
        const qb = qbs[0];
        const team = qb.team;
        if (!team) return null;
        const targets = (pool || [])
          .filter((p) =>
            p.team === team &&
            ["WR", "TE"].includes((p.position || "").toUpperCase())
          )
          .sort((a, b) => Number(b.our_proj || b.projection || 0) - Number(a.our_proj || a.projection || 0))
          .slice(0, 4)
          .map((p) => String(p.dk_id));
        if (targets.length < 2) return null;
        return { type: "if_then_at_least_n", if: [String(qb.dk_id)], players: targets, n: 2 };
      },
    },
    {
      // Sprint 60.129 — Team-scoped bring-back preset. Picks the highest
      // total-OU game on the slate and forces ≥2 of that game's road
      // team's WR/TE pool onto every lineup (classic pass-catcher pool).
      // Falls back silently if Vegas OU data isn't attached to the pool.
      key: "bring_back_high_ou_team",
      label: "Bring-Back · ≥2 of Highest-OU Road WR/TE",
      hint: "Forces ≥2 WR/TE from the highest-projected road team so every lineup has a bring-back pass-catcher pool",
      build: () => {
        // Aggregate proj by team as a Vegas-lite proxy (works even
        // without OU data attached).
        const teamProj = new Map();
        (pool || []).forEach((p) => {
          if (!p.team) return;
          const pos = (p.position || "").toUpperCase();
          if (!["QB", "WR", "TE", "RB"].includes(pos)) return;
          teamProj.set(p.team, (teamProj.get(p.team) || 0) + Number(p.our_proj || p.projection || 0));
        });
        if (teamProj.size < 2) return null;
        // Pick the team with the highest offense projection ≠ user's likely
        // primary QB stack (we skip the top team to bias toward *bring-back*
        // pool — the opposing side of the game).
        const sorted = Array.from(teamProj.entries()).sort((a, b) => b[1] - a[1]);
        const bringBackTeam = sorted[1] ? sorted[1][0] : sorted[0][0];
        return { type: "team_at_least_n", team: bringBackTeam, positions: ["WR", "TE"], n: 2 };
      },
    },
    {
      // Sprint 60.130 — Cap the field's likely chalk team at ≤2 players.
      // Uses aggregate ownership (our_own) to identify which team is
      // pooling the field's exposure and caps roster count on that team
      // so lineups avoid over-correlated chalk piles.
      key: "cap_chalk_team",
      label: "Cap Chalk Team ≤ 2",
      hint: "Caps the highest-owned team at ≤2 roster spots (any position). Diversifies away from field-heavy stacks.",
      build: () => {
        const teamOwn = new Map();
        (pool || []).forEach((p) => {
          if (!p.team) return;
          teamOwn.set(p.team, (teamOwn.get(p.team) || 0) + Number(p.our_own || p.market_mean_own || 0));
        });
        if (teamOwn.size === 0) return null;
        const [chalkTeam] = Array.from(teamOwn.entries()).sort((a, b) => b[1] - a[1])[0] || [];
        if (!chalkTeam) return null;
        return { type: "team_at_most_n", team: chalkTeam, n: 2 };
      },
    },
    {
      // Sprint 60.130 — Force ≥3 players from the highest-total-proj GAME
      // (both teams combined). Classic Milly Maker topology — pairs QB +
      // 2 pass-catchers OR QB + 1 pass-catcher + 1 bring-back. Uses the
      // new `teams: [homeTeam, awayTeam]` array so a single rule spans
      // both sides of the game.
      key: "force_3man_game_stack",
      label: "Force 3-Man Game Stack",
      hint: "Forces ≥3 players from the highest-total-projection game (either team). Classic bring-back topology in one click.",
      build: () => {
        // Group by game_id if present, else pair teams by any game context.
        const teamProj = new Map();
        (pool || []).forEach((p) => {
          if (!p.team) return;
          const pos = (p.position || "").toUpperCase();
          if (!["QB", "WR", "TE", "RB"].includes(pos)) return;
          teamProj.set(p.team, (teamProj.get(p.team) || 0) + Number(p.our_proj || p.projection || 0));
        });
        if (teamProj.size < 2) return null;
        // If game_id is available, pair teams by shared game_id.
        const gameTeams = new Map(); // game_id -> Set<team>
        (pool || []).forEach((p) => {
          const gid = p.game_id;
          if (!gid || !p.team) return;
          if (!gameTeams.has(gid)) gameTeams.set(gid, new Set());
          gameTeams.get(gid).add(p.team);
        });
        let picked = null;
        if (gameTeams.size > 0) {
          // Highest-total-proj game by summing its two teams' team-proj
          const gameScores = Array.from(gameTeams.entries())
            .filter(([, teams]) => teams.size === 2)
            .map(([gid, teams]) => {
              const arr = Array.from(teams);
              return { gid, teams: arr, score: (teamProj.get(arr[0]) || 0) + (teamProj.get(arr[1]) || 0) };
            })
            .sort((a, b) => b.score - a.score);
          if (gameScores.length > 0) picked = gameScores[0].teams;
        }
        if (!picked) {
          // Fallback — pair the top-2 teams by team-proj as a proxy game
          const sorted = Array.from(teamProj.entries()).sort((a, b) => b[1] - a[1]).slice(0, 2);
          picked = sorted.map((e) => e[0]);
        }
        if (!picked || picked.length < 2) return null;
        return {
          type: "team_at_least_n",
          teams: picked.map((t) => String(t).toUpperCase()),
          positions: ["QB", "WR", "TE", "RB"],
          n: 3,
        };
      },
    },
  ]), [pool]);

  // Sprint 60.142 — Preset active-state detection. Match each preset's
  // signature (type + n + team/players shape) against the current rules
  // array. Clicking an active preset now REMOVES the matching rule
  // instead of showing "already active" — presets act as true toggles.
  const activePresetIds = useMemo(() => {
    const byKey = new Map();
    (PRESETS || []).forEach((preset) => {
      const built = preset.build();
      if (!built) return;
      const match = (rules || []).find((r) => {
        if (r.type !== built.type || r.n !== built.n) return false;
        if (built.team || built.teams) {
          const rTeams = Array.isArray(r.teams) && r.teams.length
            ? r.teams.map((t) => String(t).toUpperCase()).sort()
            : (r.team ? [String(r.team).toUpperCase()] : []);
          const builtTeams = Array.isArray(built.teams) && built.teams.length
            ? built.teams.map((t) => String(t).toUpperCase()).sort()
            : (built.team ? [String(built.team).toUpperCase()] : []);
          return (
            JSON.stringify(rTeams) === JSON.stringify(builtTeams) &&
            JSON.stringify((r.positions || []).slice().sort()) === JSON.stringify((built.positions || []).slice().sort())
          );
        }
        return (
          JSON.stringify((r.players || []).slice().sort()) === JSON.stringify((built.players || []).slice().sort())
        );
      });
      if (match) byKey.set(preset.key, match.id);
    });
    return byKey;
  }, [PRESETS, rules]);

  const applyPreset = (preset) => {
    // If preset is currently active, clicking again REMOVES it (toggle behavior).
    const activeId = activePresetIds.get(preset.key);
    if (activeId) {
      onChange((rules || []).filter((r) => r.id !== activeId));
      toast.info(`Preset removed · ${preset.label}`);
      return;
    }
    const built = preset.build();
    if (!built) {
      toast.error(`Preset "${preset.label}" — pool doesn't have the right players yet. Load a slate first.`);
      return;
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    onChange([...(rules || []), { id, ...built }]);
    toast.success(`Added preset · ${preset.label}`);
  };

  const pidLabel = React.useMemo(() => {
    const m = new Map();
    (pool || []).forEach((p) => {
      m.set(String(p.dk_id), `${p.name} (${p.position} · ${p.team})`);
    });
    return m;
  }, [pool]);

  const searchResults = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return (pool || [])
      .filter((p) => (p.name || "").toLowerCase().includes(term) || (p.team || "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [pool, q]);

  const addTo = (bucket, id) => {
    const setter = bucket === "if" ? setIfPicks : bucket === "then" ? setThenPicks : setCountPicks;
    const cur = bucket === "if" ? ifPicks : bucket === "then" ? thenPicks : countPicks;
    if (!cur.includes(id)) setter([...cur, id]);
    setQ("");
  };
  const removeFrom = (bucket, id) => {
    const setter = bucket === "if" ? setIfPicks : bucket === "then" ? setThenPicks : setCountPicks;
    const cur = bucket === "if" ? ifPicks : bucket === "then" ? thenPicks : countPicks;
    setter(cur.filter((x) => x !== id));
  };
  const clearComposer = () => {
    setIfPicks([]); setThenPicks([]); setCountPicks([]); setN(1); setQ("");
    setTeamPick(""); setPosPicks([]);
  };

  const canAdd = isConditional
    ? ifPicks.length > 0 && thenPicks.length > 0
    : isCorrelation
      ? ifPicks.length > 0 && countPicks.length > 0 && (Number(n) || 0) > 0
      : isTeamRule
        ? !!teamPick && (Number(n) || 0) > 0
        : countPicks.length > 0;

  const addRule = () => {
    if (!canAdd) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const rule = isConditional
      ? { id, type: ruleType, if: [...ifPicks], then: [...thenPicks] }
      : isCorrelation
        ? {
            id,
            type: ruleType,
            if: [...ifPicks],
            players: [...countPicks],
            n: Math.max(1, Math.min(9, Number(n) || 1)),
          }
        : isTeamRule
          ? {
              id,
              type: ruleType,
              team: String(teamPick || "").toUpperCase(),
              positions: [...posPicks],
              n: Math.max(0, Math.min(9, Number(n) || 0)),
            }
          : { id, type: ruleType, players: [...countPicks], n: Math.max(0, Math.min(9, Number(n) || 0)) };
    onChange([...(rules || []), rule]);
    clearComposer();
  };

  const removeRule = (id) => onChange((rules || []).filter((r) => r.id !== id));

  const Chip = ({ label, onRemove, tone = "zinc" }) => (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] mono uppercase tracking-widest mr-1 mb-1 ${
      tone === "green" ? "border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/10" :
      tone === "red"   ? "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/10" :
                         "border-zinc-700 text-zinc-300"
    }`}>
      {label}
      {onRemove && (
        <button type="button" onClick={onRemove} className="hover:text-white ml-1">×</button>
      )}
    </span>
  );

  return (
    <div className="mt-3 border border-zinc-800 bg-[#0e0e0e] p-3" data-testid="raw-group-rules-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[11px] font-black uppercase tracking-widest text-white">
          Group Rules · If/Then Engine
        </div>
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
          {rules?.length || 0} active
        </div>
      </div>

      {/* Sprint 60.80 — Rule Presets · Sprint 60.142 toggle behavior */}
      <div className="mb-3 border-b border-zinc-900 pb-3" data-testid="raw-rule-presets">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mb-2">
          One-Click Presets <span className="text-zinc-600 normal-case">· click to toggle on/off</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => {
            const isActive = activePresetIds.has(p.key);
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p)}
                title={p.hint}
                data-testid={`raw-rule-preset-${p.key}`}
                data-active={isActive ? "true" : "false"}
                className={`group relative px-2.5 py-1 mono text-[10px] uppercase tracking-widest transition-colors ${
                  isActive
                    ? "border border-[#39FF14] bg-[#39FF14] text-black font-bold"
                    : "border border-zinc-800 text-zinc-300 hover:border-[#39FF14] hover:text-[#39FF14]"
                }`}
              >
                {isActive ? "✓" : "+"} {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active rules list */}
      {rules?.length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="raw-group-rules-list">
          {rules.map((r) => {
            const isCond = r.type === "if_then_exclude" || r.type === "if_then_include";
            const isCorr = r.type === "if_then_at_least_n";
            const isTeam = r.type === "team_at_least_n" || r.type === "team_at_most_n";
            const teamLabel = isTeam
              ? (Array.isArray(r.teams) && r.teams.length > 0
                  ? r.teams.map((t) => String(t).toUpperCase()).join("+")
                  : String(r.team || "").toUpperCase())
              : "";
            const verb = r.type === "if_then_exclude" ? "EXCLUDE" :
                         r.type === "if_then_include" ? "INCLUDE" :
                         r.type === "if_then_at_least_n" ? `IF · THEN ≥${r.n} OF` :
                         r.type === "team_at_least_n" ? `${teamLabel} · ≥${r.n} ${(r.positions || []).join("/") || "ANY"}` :
                         r.type === "team_at_most_n" ? `${teamLabel} · ≤${r.n} ${(r.positions || []).join("/") || "ANY"}` :
                         r.type === "exactly_n" ? `EXACTLY ${r.n}` :
                         r.type === "at_most_n" ? `AT MOST ${r.n}` : `AT LEAST ${r.n}`;
            return (
              <div key={r.id} className="flex items-start gap-2 border border-zinc-900 bg-black px-2 py-1.5">
                <div className="flex-1 text-[11px]">
                  <span className="mono text-[9px] uppercase tracking-widest text-[#39FF14] mr-2">{verb}</span>
                  {isCond ? (
                    <>
                      {(r.if || []).map((id) => (
                        <Chip key={`if-${id}`} label={pidLabel.get(String(id)) || id} tone="green" />
                      ))}
                      <span className="mono text-[9px] uppercase text-zinc-500 mx-1">→</span>
                      {(r.then || []).map((id) => (
                        <Chip key={`th-${id}`} label={pidLabel.get(String(id)) || id} tone={r.type === "if_then_exclude" ? "red" : "green"} />
                      ))}
                    </>
                  ) : isCorr ? (
                    <>
                      {(r.if || []).map((id) => (
                        <Chip key={`if-${id}`} label={pidLabel.get(String(id)) || id} tone="green" />
                      ))}
                      <span className="mono text-[9px] uppercase text-zinc-500 mx-1">→</span>
                      {(r.players || []).map((id) => (
                        <Chip key={`p-${id}`} label={pidLabel.get(String(id)) || id} tone="zinc" />
                      ))}
                    </>
                  ) : isTeam ? (
                    <span className="mono text-[10px] uppercase tracking-widest text-zinc-400">
                      dynamic pool · resolves at build time
                    </span>
                  ) : (
                    (r.players || []).map((id) => (
                      <Chip key={`p-${id}`} label={pidLabel.get(String(id)) || id} tone="zinc" />
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(r.id)}
                  data-testid={`raw-group-rule-remove-${r.id}`}
                  className="text-zinc-500 hover:text-[#FF3B30] mono text-[10px] font-black leading-none"
                  title="Remove rule"
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-zinc-900 pt-3">
        <div className="flex flex-wrap gap-1 mb-2">
          {RULE_TYPES.map((rt) => (
            <button
              key={rt.key}
              type="button"
              onClick={() => { setRuleType(rt.key); clearComposer(); }}
              data-testid={`raw-group-type-${rt.key}`}
              className={`px-2 py-1 mono text-[10px] uppercase tracking-widest border transition-colors ${
                ruleType === rt.key
                  ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {rt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Left: pick players (conditional shows IF + THEN;
              correlation shows IF + Player List + N;
              cardinality shows single bucket + N) */}
          {isConditional ? (
            <>
              <RuleBucket
                title="IF (Player A)"
                picks={ifPicks}
                pidLabel={pidLabel}
                onRemove={(id) => removeFrom("if", id)}
                testidPrefix="raw-group-if"
              />
              <RuleBucket
                title={activeType.key === "if_then_exclude" ? "THEN EXCLUDE" : "THEN INCLUDE"}
                picks={thenPicks}
                pidLabel={pidLabel}
                onRemove={(id) => removeFrom("then", id)}
                testidPrefix="raw-group-then"
                tone={activeType.key === "if_then_exclude" ? "red" : "green"}
              />
            </>
          ) : isCorrelation ? (
            <>
              <RuleBucket
                title="IF (Trigger)"
                picks={ifPicks}
                pidLabel={pidLabel}
                onRemove={(id) => removeFrom("if", id)}
                testidPrefix="raw-group-if"
                tone="green"
              />
              <div>
                <RuleBucket
                  title="THEN ≥N OF (Correlation Targets)"
                  picks={countPicks}
                  pidLabel={pidLabel}
                  onRemove={(id) => removeFrom("count", id)}
                  testidPrefix="raw-group-count"
                />
                <label className="block mt-2">
                  <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">N (minimum targets when trigger rosters)</div>
                  <input
                    type="number"
                    min="1"
                    max="9"
                    value={n}
                    onChange={(e) => setN(Math.max(1, Math.min(9, parseInt(e.target.value, 10) || 1)))}
                    data-testid="raw-group-n"
                    className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
                  />
                  <div className="text-[9px] mono text-zinc-500 mt-1 uppercase tracking-widest">
                    If any trigger is rostered, force at least {Math.max(1, Math.min(9, Number(n) || 1))} of the target list into that same lineup.
                  </div>
                </label>
              </div>
            </>
          ) : isTeamRule ? (
            <>
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">Team</div>
                <select
                  value={teamPick}
                  onChange={(e) => setTeamPick(e.target.value)}
                  data-testid="raw-group-team"
                  className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono text-white"
                >
                  <option value="">— pick a team —</option>
                  {Array.from(new Set((pool || []).map((p) => p.team).filter(Boolean))).sort().map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1 mt-3">Positions (optional filter)</div>
                <div className="flex flex-wrap gap-1">
                  {["QB", "RB", "WR", "TE", "DST"].map((pos) => {
                    const on = posPicks.includes(pos);
                    return (
                      <button
                        key={pos}
                        type="button"
                        onClick={() => setPosPicks(on ? posPicks.filter((x) => x !== pos) : [...posPicks, pos])}
                        data-testid={`raw-group-team-pos-${pos}`}
                        className={`px-2 py-0.5 mono text-[10px] uppercase tracking-widest border ${
                          on ? "bg-[#39FF14] text-black border-[#39FF14] font-bold" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                        }`}
                      >
                        {pos}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[9px] mono text-zinc-500 mt-1 uppercase tracking-widest">
                  Blank = all positions on that team qualify.
                </div>
              </div>
              <label className="block">
                <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">N (target count)</div>
                <input
                  type="number"
                  min="0"
                  max="9"
                  value={n}
                  onChange={(e) => setN(Math.max(0, Math.min(9, parseInt(e.target.value, 10) || 0)))}
                  data-testid="raw-group-n"
                  className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
                />
                <div className="text-[9px] mono text-zinc-500 mt-1 uppercase tracking-widest">
                  {ruleType === "team_at_least_n"
                    ? `Force ≥${n} ${(posPicks.length ? posPicks.join("/") : "player")} from ${teamPick || "the picked team"} on every lineup.`
                    : `Cap ${teamPick || "the picked team"}'s ${(posPicks.length ? posPicks.join("/") : "players")} at ≤${n} per lineup.`}
                </div>
              </label>
            </>
          ) : (
            <>
              <RuleBucket
                title="Player List"
                picks={countPicks}
                pidLabel={pidLabel}
                onRemove={(id) => removeFrom("count", id)}
                testidPrefix="raw-group-count"
              />
              <label className="block">
                <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">N (target count)</div>
                <input
                  type="number"
                  min="0"
                  max="9"
                  value={n}
                  onChange={(e) => setN(Math.max(0, Math.min(9, parseInt(e.target.value, 10) || 0)))}
                  data-testid="raw-group-n"
                  className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-2 py-1.5 text-sm mono"
                />
                <div className="text-[9px] mono text-zinc-500 mt-1 uppercase tracking-widest">
                  {ruleType === "exactly_n" ? `Use exactly ${n} from the list on every lineup.` :
                   ruleType === "at_most_n" ? `Cap the list at ${n} players per lineup.` :
                                              `Force at least ${n} players from the list on every lineup.`}
                </div>
              </label>
            </>
          )}
        </div>

        {/* Player search — adds to the "active" bucket. Hidden for team-scoped
            rules where the composer picks by (team, positions) instead. */}
        {!isTeamRule && (
        <div className="mt-3 relative">
          <input
            type="text"
            placeholder="Type a player name to add to the rule…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="raw-group-search"
            className="w-full bg-black border border-zinc-800 focus:border-[#39FF14] outline-none px-3 py-1.5 text-sm mono"
          />
          {searchResults.length > 0 && (
            <div className="absolute z-20 mt-1 w-full bg-[#0a0a0a] border border-zinc-800 max-h-56 overflow-y-auto">
              {searchResults.map((p) => (
                <div key={p.dk_id} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50">
                  <div className="text-xs mono text-white">
                    {p.name} <span className="text-zinc-500">· {p.position} · {p.team} · ${p.salary?.toLocaleString?.()}</span>
                  </div>
                  <div className="flex gap-1">
                    {isConditional ? (
                      <>
                        <button
                          type="button"
                          onClick={() => addTo("if", String(p.dk_id))}
                          data-testid={`raw-group-add-if-${p.dk_id}`}
                          className="px-2 py-0.5 text-[9px] mono uppercase tracking-widest border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                        >+ IF</button>
                        <button
                          type="button"
                          onClick={() => addTo("then", String(p.dk_id))}
                          data-testid={`raw-group-add-then-${p.dk_id}`}
                          className={`px-2 py-0.5 text-[9px] mono uppercase tracking-widest border ${
                            activeType.key === "if_then_exclude"
                              ? "border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-black"
                              : "border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                          }`}
                        >+ THEN</button>
                      </>
                    ) : isCorrelation ? (
                      <>
                        <button
                          type="button"
                          onClick={() => addTo("if", String(p.dk_id))}
                          data-testid={`raw-group-add-if-${p.dk_id}`}
                          className="px-2 py-0.5 text-[9px] mono uppercase tracking-widest border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                        >+ IF</button>
                        <button
                          type="button"
                          onClick={() => addTo("count", String(p.dk_id))}
                          data-testid={`raw-group-add-count-${p.dk_id}`}
                          className="px-2 py-0.5 text-[9px] mono uppercase tracking-widest border border-zinc-500/60 text-zinc-300 hover:bg-zinc-300 hover:text-black"
                        >+ LIST</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => addTo("count", String(p.dk_id))}
                        data-testid={`raw-group-add-count-${p.dk_id}`}
                        className="px-2 py-0.5 text-[9px] mono uppercase tracking-widest border border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                      >+ ADD</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={addRule}
            disabled={!canAdd}
            data-testid="raw-group-add-rule"
            className="bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-30 text-black font-black uppercase tracking-widest text-[10px] px-4 py-2"
          >
            + Add Rule
          </button>
          <button
            type="button"
            onClick={clearComposer}
            className="text-[10px] mono uppercase tracking-widest text-zinc-500 hover:text-white"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

function RuleBucket({ title, picks, pidLabel, onRemove, testidPrefix, tone = "zinc" }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 mb-1">{title}</div>
      <div className="min-h-[38px] bg-black border border-zinc-800 p-1.5" data-testid={`${testidPrefix}-bucket`}>
        {(picks || []).length === 0 && (
          <div className="text-[10px] mono text-zinc-600 uppercase tracking-widest px-1 py-1">— none —</div>
        )}
        {(picks || []).map((id) => (
          <span
            key={id}
            className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] mono uppercase tracking-widest mr-1 mb-1 ${
              tone === "red"   ? "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/10" :
              tone === "green" ? "border-[#39FF14]/40 text-[#39FF14] bg-[#39FF14]/10" :
                                 "border-zinc-700 text-zinc-300"
            }`}
          >
            {pidLabel.get(String(id)) || id}
            <button type="button" onClick={() => onRemove(id)} className="hover:text-white ml-1" data-testid={`${testidPrefix}-remove-${id}`}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}
